import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  AssignmentReturn as ReturnIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { reportsService, type AdminStats } from '../services/reportsService';
import { loanService, type BookLoan, type LoanRequest } from '../services/loanService';
import { useAppStore } from '../stores/useAppStore';
import { canManageBooks } from '../utils/rbac';
import { OverdueBooksPage } from './OverdueBooksPage';
import { useDebounce } from '../hooks/usePerformance';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { ExportDialog } from '../components/ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { isStudent } from '../utils/rbac';
import { logger } from '../utils/logger';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`book-loans-tabpanel-${index}`}
      aria-labelledby={`book-loans-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

export const BookLoansPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const canProcess = canManageBooks(userRoles);
  const canExport = !isStudent(userRoles);

  const [activeTab, setActiveTab] = useState(0);
  const [loans, setLoans] = useState<BookLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [loansPage, setLoansPage] = useState(1);
  const [loansPageSize] = useState(10);
  const [loansTotalCount, setLoansTotalCount] = useState(0);
  const [loansTotalPages, setLoansTotalPages] = useState(1);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Stats for Tab 2 (Overdue books)
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [loadingOverdue, setLoadingOverdue] = useState(false);

  // Tab 0 (Loan requests - for librarians)
  const [loanRequests, setLoanRequests] = useState<LoanRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [errorRequests, setErrorRequests] = useState<string | null>(null);
  const [requestStatusFilter, setRequestStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('');
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPageSize] = useState(10);
  const [requestsTotalCount, setRequestsTotalCount] = useState(0);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [bookRequestInfoDialogOpen, setBookRequestInfoDialogOpen] = useState(false);
  const [selectedRequestForInfo, setSelectedRequestForInfo] = useState<LoanRequest | null>(null);

  // Export dialog states
  const [exportCurrentLoansDialogOpen, setExportCurrentLoansDialogOpen] = useState(false);
  const [exportRequestsDialogOpen, setExportRequestsDialogOpen] = useState(false);
  const [exportHistoryDialogOpen, setExportHistoryDialogOpen] = useState(false);
  const [exportOverdueDialogOpen, setExportOverdueDialogOpen] = useState(false);

  // Tab 2 (Loan history)
  const [loanHistory, setLoanHistory] = useState<BookLoan[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorHistory, setErrorHistory] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(10);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  // Search filters for Tab 0 (Sách mượn)
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Search filters for Tab 1 (Lịch sử mượn)
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState<Date | null>(null);
  const [historyEndDate, setHistoryEndDate] = useState<Date | null>(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('');

  // Debounced filters for Tab 0 (Sách mượn)
  const debouncedSearchTerm = useDebounce(searchTerm, 800);
  const debouncedStartDate = useDebounce(startDate, 800);
  const debouncedEndDate = useDebounce(endDate, 800);

  // Debounced filters for Tab 1 (Lịch sử mượn)
  const debouncedHistorySearchTerm = useDebounce(historySearchTerm, 800);
  const debouncedHistoryStartDate = useDebounce(historyStartDate, 800);
  const debouncedHistoryEndDate = useDebounce(historyEndDate, 800);

  const loadLoans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert Date objects to YYYY-MM-DD format
      const startDateStr = debouncedStartDate
        ? `${debouncedStartDate.getFullYear()}-${String(debouncedStartDate.getMonth() + 1).padStart(2, '0')}-${String(debouncedStartDate.getDate()).padStart(2, '0')}`
        : undefined;
      const endDateStr = debouncedEndDate
        ? `${debouncedEndDate.getFullYear()}-${String(debouncedEndDate.getMonth() + 1).padStart(2, '0')}-${String(debouncedEndDate.getDate()).padStart(2, '0')}`
        : undefined;

      const result = await loanService.getAllLoans({
        search: debouncedSearchTerm || undefined,
        startDate: startDateStr,
        endDate: endDateStr,
        page: loansPage,
        pageSize: loansPageSize
      });
      setLoans(result.loans);
      setLoansTotalCount(result.totalCount);
      setLoansTotalPages(result.totalPages);
    } catch (err) {
      logger.error('Error fetching loans:', err);
      setError('Không thể tải danh sách sách mượn. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, debouncedStartDate, debouncedEndDate, loansPage, loansPageSize]);

  const fetchOverdueBooks = useCallback(async () => {
    try {
      setLoadingOverdue(true);
      const stats = await reportsService.getAdminStats();
      setAdminStats(stats);
    } catch (err) {
      logger.error('Error fetching overdue books:', err);
    } finally {
      setLoadingOverdue(false);
    }
  }, []);

  const loadLoanRequests = useCallback(async () => {
    try {
      setLoadingRequests(true);
      setErrorRequests(null);

      const status = requestStatusFilter || undefined;
      const result = await loanService.getLoanRequests({
        status,
        page: requestsPage,
        pageSize: requestsPageSize
      });
      setLoanRequests(result.requests);
      setRequestsTotalCount(result.totalCount);
      setRequestsTotalPages(result.totalPages);
    } catch (err) {
      logger.error('Error fetching loan requests:', err);
      // UX: For a new school (no data) or transient backend issues, show empty state instead of a hard error banner.
      setLoanRequests([]);
      setRequestsTotalCount(0);
      setRequestsTotalPages(1);
      setErrorRequests(null);
    } finally {
      setLoadingRequests(false);
    }
  }, [requestStatusFilter, requestsPage, requestsPageSize]);

  const loadLoanHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      setErrorHistory(null);

      // Convert Date objects to YYYY-MM-DD format
      const historyStartDateStr = debouncedHistoryStartDate
        ? `${debouncedHistoryStartDate.getFullYear()}-${String(debouncedHistoryStartDate.getMonth() + 1).padStart(2, '0')}-${String(debouncedHistoryStartDate.getDate()).padStart(2, '0')}`
        : undefined;
      const historyEndDateStr = debouncedHistoryEndDate
        ? `${debouncedHistoryEndDate.getFullYear()}-${String(debouncedHistoryEndDate.getMonth() + 1).padStart(2, '0')}-${String(debouncedHistoryEndDate.getDate()).padStart(2, '0')}`
        : undefined;

      const result = await loanService.getLoanHistory({
        search: debouncedHistorySearchTerm || undefined,
        startDate: historyStartDateStr,
        endDate: historyEndDateStr,
        status: historyStatusFilter ? (historyStatusFilter as 'returned' | 'not_returned' | 'on_time' | 'overdue') : undefined,
        page: historyPage,
        pageSize: historyPageSize
      });
      setLoanHistory(result.loans);
      setHistoryTotalCount(result.totalCount);
      setHistoryTotalPages(result.totalPages);
    } catch (err) {
      logger.error('Error fetching loan history:', err);
      setErrorHistory('Không thể tải lịch sử mượn. Vui lòng thử lại sau.');
    } finally {
      setLoadingHistory(false);
    }
  }, [debouncedHistorySearchTerm, debouncedHistoryStartDate, debouncedHistoryEndDate, historyStatusFilter, historyPage, historyPageSize]);

  useEffect(() => {
    if (activeTab === 0 && canProcess) {
      loadLoans();
    } else if (activeTab === 1 && canProcess) {
      loadLoanRequests();
    } else if (activeTab === 2 && canProcess) {
      loadLoanHistory();
    } else if (activeTab === 3 && canProcess) {
      fetchOverdueBooks();
    }
  }, [activeTab, canProcess, loadLoans, loadLoanRequests, fetchOverdueBooks, loadLoanHistory, loansPage, requestsPage, historyPage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedLoanIds([]);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allLoanIds = loans.map(loan => loan.loanId || loan.id);
      setSelectedLoanIds(allLoanIds);
    } else {
      setSelectedLoanIds([]);
    }
  };

  const handleSelectLoan = (loanId: string) => {
    setSelectedLoanIds(prev => {
      if (prev.includes(loanId)) {
        return prev.filter(id => id !== loanId);
      } else {
        return [...prev, loanId];
      }
    });
  };

  const handleSelectAllRequests = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Only select pending requests
      const pendingRequestIds = loanRequests
        .filter(request => request.status === 'pending')
        .map(request => request.id);
      setSelectedRequestIds(pendingRequestIds);
    } else {
      setSelectedRequestIds([]);
    }
  };

  const handleSelectRequest = (requestId: string) => {
    // Only allow selecting pending requests
    const request = loanRequests.find(r => r.id === requestId);
    if (request && request.status !== 'pending') {
      return; // Don't allow selecting non-pending requests
    }

    setSelectedRequestIds(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };

  const handleReturnClick = () => {
    if (selectedLoanIds.length === 0) return;
    setReturnDialogOpen(true);
  };

  const handleReturnSingleLoan = (loanId: string) => {
    setSelectedLoanIds([loanId]);
    setReturnDialogOpen(true);
  };

  const handleReturnConfirm = async () => {
    try {
      const response = await loanService.returnBooks(selectedLoanIds);
      if (response.success) {
        showSuccess(`Đã trả ${response.returnedCount} sách thành công`);
        setReturnDialogOpen(false);
        setSelectedLoanIds([]);
        loadLoans();
      } else {
        showError(response.message || 'Không thể trả sách');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể trả sách');
    }
  };

  const handleReturnCancel = () => {
    setReturnDialogOpen(false);
    setSelectedLoanIds([]);
  };

  const handleApproveRequest = async (requestId: string) => {
    try {
      const response = await loanService.approveLoanRequest(requestId);
      if (response.success) {
        showSuccess(response.message);
        loadLoanRequests();
        loadLoans(); // Refresh loans list
      } else {
        showError(response.message || 'Không thể duyệt yêu cầu');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể duyệt yêu cầu');
    }
  };

  const handleBulkApproveRequests = async () => {
    if (selectedRequestIds.length === 0) return;
    // Only process pending requests
    const pendingIds = selectedRequestIds.filter(id => {
      const request = loanRequests.find(r => r.id === id);
      return request && request.status === 'pending';
    });
    if (pendingIds.length === 0) return;

    try {
      const approvePromises = pendingIds.map(id => loanService.approveLoanRequest(id));
      await Promise.all(approvePromises);
      showSuccess(`Đã duyệt ${pendingIds.length} yêu cầu thành công`);
      setSelectedRequestIds([]);
      loadLoanRequests();
      loadLoans();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể duyệt yêu cầu');
    }
  };

  const handleBulkRejectRequests = () => {
    if (selectedRequestIds.length === 0) return;
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectClick = (requestId: string) => {
    setSelectedRequestId(requestId);
    setSelectedRequestIds([]); // Clear bulk selection when rejecting single request
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    // If bulk reject (selectedRequestIds.length > 0), reject all selected requests
    if (selectedRequestIds.length > 0) {
      // Only process pending requests
      const pendingIds = selectedRequestIds.filter(id => {
        const request = loanRequests.find(r => r.id === id);
        return request && request.status === 'pending';
      });

      if (pendingIds.length === 0) {
        setRejectDialogOpen(false);
        setSelectedRequestIds([]);
        setRejectionReason('');
        return;
      }

      try {
        const rejectPromises = pendingIds.map(id =>
          loanService.rejectLoanRequest(id, rejectionReason)
        );
        await Promise.all(rejectPromises);
        showSuccess(`Đã từ chối ${pendingIds.length} yêu cầu thành công`);
        setRejectDialogOpen(false);
        setSelectedRequestIds([]);
        setRejectionReason('');
        loadLoanRequests();
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Không thể từ chối yêu cầu');
      }
      return;
    }

    // Single reject
    if (!selectedRequestId) return;
    try {
      const response = await loanService.rejectLoanRequest(selectedRequestId, rejectionReason);
      if (response.success) {
        showSuccess(response.message);
        setRejectDialogOpen(false);
        setSelectedRequestId(null);
        setRejectionReason('');
        loadLoanRequests();
      } else {
        showError(response.message || 'Không thể từ chối yêu cầu');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể từ chối yêu cầu');
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedRequestId(null);
    setSelectedRequestIds([]); // Clear bulk selection when canceling
    setRejectionReason('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN');
    } catch {
      return dateString;
    }
  };

  if (!canProcess) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Bạn không có quyền truy cập trang này. Chỉ Admin, Người kiểm duyệt và Thủ thư mới có quyền xem và quản lý sách mượn.
        </Alert>
      </Box>
    );
  }

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
  };

  const handleClearHistoryFilters = () => {
    setHistorySearchTerm('');
    setHistoryStartDate(null);
    setHistoryEndDate(null);
    setHistoryStatusFilter('');
  };

  const handleRefresh = () => {
    // Clear all filters and reload current tab data
    if (activeTab === 0) {
      setSearchTerm('');
      setStartDate(null);
      setEndDate(null);
      loadLoans();
    } else if (activeTab === 1) {
      setRequestStatusFilter('');
      loadLoanRequests();
    } else if (activeTab === 2) {
      setHistorySearchTerm('');
      setHistoryStartDate(null);
      setHistoryEndDate(null);
      setHistoryStatusFilter('');
      loadLoanHistory();
    } else if (activeTab === 3) {
      fetchOverdueBooks();
    }
  };

  // Export handlers
  const handleExportCurrentLoans = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await loanService.exportCurrentLoans(dateFrom, dateTo);
  };

  const handleExportRequests = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await loanService.exportLoanRequests(dateFrom, dateTo, requestStatusFilter || undefined);
  };

  const handleExportHistory = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await loanService.exportLoanHistory(dateFrom, dateTo, historyStatusFilter || undefined);
  };

  const handleExportOverdue = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await loanService.exportOverdueBooks(dateFrom, dateTo);
  };

  // Export column definitions
  const currentLoansExportColumns: ExportColumn[] = [
    { key: 'book_title', label: 'Tên sách', width: 30 },
    { key: 'book_author', label: 'Tác giả', width: 25 },
    { key: 'book_isbn', label: 'ISBN', width: 20 },
    { key: 'user_fullname', label: 'Người mượn', width: 25 },
    { key: 'user_email', label: 'Email', width: 25 },
    { key: 'user_phone', label: 'Số điện thoại', width: 15 },
    { key: 'loandate', label: 'Ngày mượn', width: 15 },
    { key: 'duedate', label: 'Ngày hết hạn', width: 15 }
  ];

  const requestsExportColumns: ExportColumn[] = [
    { key: 'book_title', label: 'Tên sách', width: 30 },
    { key: 'book_author', label: 'Tác giả', width: 25 },
    { key: 'user_fullname', label: 'Người mượn', width: 25 },
    { key: 'user_email', label: 'Email', width: 25 },
    { key: 'user_phone', label: 'Số điện thoại', width: 15 },
    { key: 'requesteddate', label: 'Ngày yêu cầu', width: 15 },
    { key: 'duedate', label: 'Ngày hết hạn dự kiến', width: 18 },
    { key: 'status', label: 'Trạng thái', width: 12 },
    { key: 'revieweddate', label: 'Ngày duyệt', width: 15 },
    { key: 'reviewer_name', label: 'Người duyệt', width: 20 },
    { key: 'rejectionreason', label: 'Lý do từ chối', width: 30 }
  ];

  const historyExportColumns: ExportColumn[] = [
    { key: 'book_title', label: 'Tên sách', width: 30 },
    { key: 'book_author', label: 'Tác giả', width: 25 },
    { key: 'book_isbn', label: 'ISBN', width: 20 },
    { key: 'user_fullname', label: 'Người mượn', width: 25 },
    { key: 'user_email', label: 'Email', width: 25 },
    { key: 'user_phone', label: 'Số điện thoại', width: 15 },
    { key: 'loandate', label: 'Ngày mượn', width: 15 },
    { key: 'duedate', label: 'Ngày hết hạn', width: 15 },
    { key: 'returndate', label: 'Ngày trả', width: 15 },
    { key: 'return_status', label: 'Trạng thái trả', width: 15 }
  ];

  const overdueExportColumns: ExportColumn[] = [
    { key: 'book_title', label: 'Tên sách', width: 30 },
    { key: 'book_author', label: 'Tác giả', width: 25 },
    { key: 'book_isbn', label: 'ISBN', width: 20 },
    { key: 'user_fullname', label: 'Người mượn', width: 25 },
    { key: 'user_email', label: 'Email', width: 25 },
    { key: 'user_phone', label: 'Số điện thoại', width: 15 },
    { key: 'duedate', label: 'Ngày hết hạn', width: 15 },
    { key: 'days_overdue', label: 'Số ngày quá hạn', width: 15 }
  ];

  return (
    <Box p={3}>

      {/* Thống kê Tổng Quan - Hiển thị trên cả 3 tabs - Đặt trước Tabs */}
      {(() => {
        if (activeTab === 0) {
          // Tab 0: Sách mượn
          const overdueCount = loans.filter(loan => {
            if (!loan.dueDate) return false;
            const dueDate = new Date(loan.dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < today;
          }).length;
          const notOverdueCount = loans.filter(loan => {
            if (!loan.dueDate) return false;
            const dueDate = new Date(loan.dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate >= today;
          }).length;

          return (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" gutterBottom>
                  Thống Kê Tổng Quan
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {loans.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tổng số sách đang mượn
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error">
                      {overdueCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sách quá hạn
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {notOverdueCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sách chưa quá hạn
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        } else if (activeTab === 1) {
          // Tab 1: DUYỆT SÁCH
          const pendingCount = loanRequests.filter(r => r.status === 'pending').length;
          const approvedCount = loanRequests.filter(r => r.status === 'approved').length;
          const rejectedCount = loanRequests.filter(r => r.status === 'rejected').length;

          return (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" gutterBottom>
                  Thống Kê Tổng Quan
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {loanRequests.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tổng số yêu cầu
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {pendingCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Chờ duyệt
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {approvedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Đã duyệt
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error">
                      {rejectedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Từ chối
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        } else if (activeTab === 2) {
          // Tab 2: Lịch sử mượn
          const onTimeCount = loanHistory.filter(loan => loan.returnStatus === 'on_time').length;
          const overdueCount = loanHistory.filter(loan => loan.returnStatus === 'overdue').length;
          const returnedCount = loanHistory.filter(loan => loan.returnDate !== null).length;
          const notReturnedCount = loanHistory.filter(loan => loan.returnDate === null).length;

          return (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" gutterBottom>
                  Thống Kê Tổng Quan
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {loanHistory.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tổng số lượt mượn
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {onTimeCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trả đúng hạn
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error">
                      {overdueCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trả trễ hạn
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {returnedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Đã trả
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {notReturnedCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Chưa trả
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        } else {
          // Tab 3: Sách quá hạn
          const overdueBooks = adminStats?.overdueBooks || [];

          // Tính số lượng sách quá hạn theo từng khoảng thời gian
          const overdue1Week = overdueBooks.filter(book => book.daysOverdue > 0 && book.daysOverdue <= 7).length;
          const overdue1Month = overdueBooks.filter(book => book.daysOverdue > 7 && book.daysOverdue <= 30).length;
          const overdue1Year = overdueBooks.filter(book => book.daysOverdue > 30 && book.daysOverdue <= 365).length;

          // Tính số ngày trả chậm trung bình (mặc định là 0 nếu không có dữ liệu)
          const avgDaysOverdue = overdueBooks.length > 0
            ? Math.round(overdueBooks.reduce((sum, book) => sum + (book.daysOverdue || 0), 0) / overdueBooks.length)
            : 0;

          return (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Typography variant="h6" gutterBottom>
                  Thống Kê Tổng Quan
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error">
                      {overdueBooks.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tổng số sách trả chậm
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {avgDaysOverdue}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Số ngày trả chậm trung bình
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#ffa726' }}> {/* Màu vàng */}
                      {overdue1Week}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quá hạn 1 tuần
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#ec407a' }}> {/* Màu hồng */}
                      {overdue1Month}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quá hạn 1 tháng
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ color: '#e53935' }}> {/* Màu đỏ */}
                      {overdue1Year}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quá hạn 1 năm
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        }
      })()}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Sách mượn" />
          <Tab label="DUYỆT SÁCH" />
          <Tab label="Lịch sử mượn" />
          <Tab label="Sách quá hạn" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    size="small"
                    placeholder="Tìm kiếm tổng hợp (Tên sách, Tên người mượn, số điện thoại, email)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flexGrow: 1, minWidth: 250 }}
                  />
                  <DatePicker
                    label="Từ ngày"
                    value={startDate}
                    onChange={(newDate) => setStartDate(newDate)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 150 }
                      }
                    }}
                  />
                  <DatePicker
                    label="Đến ngày"
                    value={endDate}
                    onChange={(newDate) => setEndDate(newDate)}
                    minDate={startDate || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 150 }
                      }
                    }}
                  />
                  <Tooltip title="Làm mới" arrow>
                    <IconButton
                      color="primary"
                      onClick={handleRefresh}
                      aria-label="Làm mới"
                      size="small"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canExport && (
                    <Tooltip title="Xuất Excel" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => setExportCurrentLoansDialogOpen(true)}
                        aria-label="Xuất Excel"
                        size="small"
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {selectedLoanIds.length > 0 && (
                    <Tooltip title={`Trả sách (${selectedLoanIds.length})`} arrow>
                      <IconButton
                        color="primary"
                        onClick={handleReturnClick}
                        aria-label="Trả sách"
                        size="small"
                      >
                        <ReturnIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </LocalizationProvider>
            </Paper>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : loans.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Không có sách nào đang mượn
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
                <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '32%' }}>Tên Sách</TableCell>
                      <TableCell sx={{ width: '23%' }}>Người Mượn</TableCell>
                      <TableCell sx={{ width: '20%' }}>Ngày mượn/hết hạn</TableCell>
                      <TableCell align="center" sx={{ width: '10%' }}>Hành Động</TableCell>
                      <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                        <Checkbox
                          indeterminate={selectedLoanIds.length > 0 && selectedLoanIds.length < loans.length}
                          checked={loans.length > 0 && selectedLoanIds.length === loans.length}
                          onChange={handleSelectAll}
                          size="small"
                          sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loans.map((loan) => {
                      const loanId = loan.loanId || loan.id;
                      const isSelected = selectedLoanIds.includes(loanId);

                      return (
                        <TableRow key={loanId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {loan.book.title}
                            </Typography>
                            {loan.book.author && (
                              <Typography variant="caption" color="text.secondary">
                                {loan.book.author}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                <Box>
                                  {loan.user.email && (
                                    <Typography variant="caption" display="block">
                                      Email: {loan.user.email}
                                    </Typography>
                                  )}
                                  {loan.user.phoneNumber && (
                                    <Typography variant="caption" display="block">
                                      SĐT: {loan.user.phoneNumber}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Typography variant="body2" sx={{ cursor: 'help' }}>
                                {loan.user.fullName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(loan.loanDate)} - {formatDate(loan.dueDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Trả sách">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReturnSingleLoan(loanId);
                                }}
                              >
                                <ReturnIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectLoan(loanId)}
                              size="small"
                              sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {loansTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Pagination
                  count={loansTotalPages}
                  page={loansPage}
                  onChange={(_, value) => setLoansPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    value={requestStatusFilter}
                    label="Trạng thái"
                    onChange={(e) => setRequestStatusFilter(e.target.value as 'pending' | 'approved' | 'rejected' | '')}
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="pending">Chờ duyệt</MenuItem>
                    <MenuItem value="approved">Đã duyệt</MenuItem>
                    <MenuItem value="rejected">Từ chối</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Làm mới" arrow>
                  <IconButton
                    color="primary"
                    onClick={handleRefresh}
                    aria-label="Làm mới"
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canExport && (
                  <Tooltip title="Xuất Excel" arrow>
                    <IconButton
                      color="primary"
                      onClick={() => setExportRequestsDialogOpen(true)}
                      aria-label="Xuất Excel"
                      size="small"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canProcess && (() => {
                  // Only count pending requests in selectedRequestIds
                  const pendingSelectedCount = selectedRequestIds.filter(id => {
                    const request = loanRequests.find(r => r.id === id);
                    return request && request.status === 'pending';
                  }).length;

                  return pendingSelectedCount >= 2 ? (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleBulkApproveRequests}
                      >
                        Duyệt ({pendingSelectedCount})
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleBulkRejectRequests}
                      >
                        Từ chối ({pendingSelectedCount})
                      </Button>
                    </>
                  ) : null;
                })()}
              </Box>

              {/* Legend (màu chấm tròn khớp với "Thống Kê Tổng Quan") */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'warning.main' }} />
                  <Typography variant="caption" color="text.secondary">Chờ duyệt</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                  <Typography variant="caption" color="text.secondary">Đã duyệt</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
                  <Typography variant="caption" color="text.secondary">Từ chối</Typography>
                </Box>
              </Box>
            </Paper>

            {loadingRequests ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : errorRequests ? (
              <Alert severity="error">{errorRequests}</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
                <Table sx={{ width: '100%', tableLayout: 'fixed', minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      {canProcess && (
                        <TableCell padding="none" align="center" sx={{ width: '4%' }}>
                          {(() => {
                            const pendingRequests = loanRequests.filter(r => r.status === 'pending');
                            const pendingSelectedCount = selectedRequestIds.filter(id => {
                              const request = loanRequests.find(r => r.id === id);
                              return request && request.status === 'pending';
                            }).length;
                            return pendingRequests.length > 0 ? (
                              <Checkbox
                                indeterminate={pendingSelectedCount > 0 && pendingSelectedCount < pendingRequests.length}
                                checked={pendingRequests.length > 0 && pendingSelectedCount === pendingRequests.length}
                                onChange={handleSelectAllRequests}
                                size="small"
                                sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                              />
                            ) : null;
                          })()}
                        </TableCell>
                      )}
                      <TableCell sx={{ width: '23%' }}>Tên Sách</TableCell>
                      <TableCell sx={{ width: '12%' }}>Người Yêu Cầu</TableCell>
                      <TableCell sx={{ width: '18%' }}>Ngày mượn - Trả</TableCell>
                      <TableCell sx={{ width: '8%' }}>SL Có Sẵn</TableCell>
                      <TableCell sx={{ width: '12%' }}>Người Duyệt</TableCell>
                      <TableCell sx={{ width: '23%' }}>Thao Tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loanRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canProcess ? 7 : 6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            Chưa có nội dung
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      loanRequests.map((request) => {
                        const isSelected = selectedRequestIds.includes(request.id);
                        const isPending = request.status === 'pending';
                        return (
                          <TableRow key={request.id} hover>
                            {canProcess && (
                              <TableCell padding="none" align="center">
                                {isPending ? (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleSelectRequest(request.id)}
                                    size="small"
                                    sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                                  />
                                ) : null}
                              </TableCell>
                            )}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Tooltip
                                  title={
                                    request.status === 'pending'
                                      ? 'Chờ duyệt'
                                      : request.status === 'approved'
                                        ? 'Đã duyệt'
                                        : 'Từ chối'
                                  }
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      flexShrink: 0,
                                      bgcolor:
                                        request.status === 'pending'
                                          ? 'warning.main'
                                          : request.status === 'approved'
                                            ? 'success.main'
                                            : 'error.main'
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title={request.book.title} arrow disableHoverListener={request.book.title.length <= 40}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    sx={{
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'block',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                        color: 'primary.main'
                                      }
                                    }}
                                    onClick={() => {
                                      setSelectedRequestForInfo(request);
                                      setBookRequestInfoDialogOpen(true);
                                    }}
                                  >
                                    {request.book.title}
                                  </Typography>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2.25, display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {request.book.author}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  <Box>
                                    {request.user.email && (
                                      <Typography variant="caption" display="block">
                                        Email: {request.user.email}
                                      </Typography>
                                    )}
                                    {request.user.phoneNumber && (
                                      <Typography variant="caption" display="block">
                                        SĐT: {request.user.phoneNumber}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Typography variant="body2" sx={{ cursor: 'help' }}>
                                  {request.user.fullName}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(request.requestedDate)} - {formatDate(request.dueDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {request.book.availableCopies}/{request.book.totalCopies}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {request.reviewer ? (
                                <>
                                  <Typography variant="body2">{request.reviewer.fullName}</Typography>
                                  {request.reviewedDate && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {formatDate(request.reviewedDate)}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {request.status === 'pending' && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <Tooltip title="Duyệt" placement="top">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleApproveRequest(request.id)}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Từ chối" placement="top">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRejectClick(request.id)}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                              {request.status === 'rejected' && request.rejectionReason && (
                                <Tooltip title={request.rejectionReason}>
                                  <Typography variant="caption" color="error">
                                    {request.rejectionReason}
                                  </Typography>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {requestsTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Pagination
                  count={requestsTotalPages}
                  page={requestsPage}
                  onChange={(_, value) => setRequestsPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    size="small"
                    placeholder="Tìm kiếm tổng hợp (Tên sách, Tên người mượn, số điện thoại, email)..."
                    value={historySearchTerm}
                    onChange={(e) => setHistorySearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flexGrow: 1, minWidth: 250 }}
                  />
                  <DatePicker
                    label="Từ ngày"
                    value={historyStartDate}
                    onChange={(newDate) => setHistoryStartDate(newDate)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 120, width: 120 }
                      }
                    }}
                  />
                  <DatePicker
                    label="Đến ngày"
                    value={historyEndDate}
                    onChange={(newDate) => setHistoryEndDate(newDate)}
                    minDate={historyStartDate || undefined}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { minWidth: 120, width: 120 }
                      }
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Trạng thái</InputLabel>
                    <Select
                      value={historyStatusFilter}
                      label="Trạng thái"
                      onChange={(e) => setHistoryStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">Tất cả</MenuItem>
                      <MenuItem value="on_time">Đúng hạn</MenuItem>
                      <MenuItem value="overdue">Trễ hạn</MenuItem>
                      <MenuItem value="returned">Đã trả</MenuItem>
                      <MenuItem value="not_returned">Chưa trả</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Làm mới" arrow>
                    <IconButton
                      color="primary"
                      onClick={handleRefresh}
                      aria-label="Làm mới"
                      size="small"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canExport && (
                    <Tooltip title="Xuất Excel" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => setExportHistoryDialogOpen(true)}
                        aria-label="Xuất Excel"
                        size="small"
                      >
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </LocalizationProvider>
            </Paper>

            {loadingHistory ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : errorHistory ? (
              <Alert severity="error">{errorHistory}</Alert>
            ) : loanHistory.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Không có lịch sử mượn
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
                <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '32%' }}>Tên Sách</TableCell>
                      <TableCell sx={{ width: '18%' }}>Người Mượn</TableCell>
                      <TableCell sx={{ width: '20%' }}>Ngày mượn/hết hạn</TableCell>
                      <TableCell sx={{ width: '12%' }}>Ngày Trả</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loanHistory.map((loan) => {
                      const loanId = loan.loanId || loan.id;

                      return (
                        <TableRow key={loanId} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Tooltip
                                title={
                                  loan.returnStatus === 'on_time'
                                    ? 'Đúng hạn'
                                    : loan.returnStatus === 'overdue'
                                      ? 'Trễ hạn'
                                      : loan.returnDate
                                        ? 'Đã trả'
                                        : 'Chưa trả'
                                }
                                arrow
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    bgcolor:
                                      loan.returnStatus === 'on_time'
                                        ? 'success.main'
                                        : loan.returnStatus === 'overdue'
                                          ? 'error.main'
                                          : loan.returnDate
                                            ? 'info.main'
                                            : 'warning.main'
                                  }}
                                />
                              </Tooltip>
                              {loan.book.title.length > 40 ? (
                                <Tooltip title={loan.book.title} arrow>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    sx={{
                                      maxWidth: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'block',
                                      cursor: 'default'
                                    }}
                                  >
                                    {loan.book.title.substring(0, 40)}...
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {loan.book.title}
                                </Typography>
                              )}
                            </Box>
                            {loan.book.author && (
                              <Typography variant="caption" color="text.secondary">
                                {loan.book.author}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                <Box>
                                  {loan.user.email && (
                                    <Typography variant="caption" display="block">
                                      Email: {loan.user.email}
                                    </Typography>
                                  )}
                                  {loan.user.phoneNumber && (
                                    <Typography variant="caption" display="block">
                                      SĐT: {loan.user.phoneNumber}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Typography variant="body2" sx={{ cursor: 'help' }}>
                                {loan.user.fullName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(loan.loanDate)} - {formatDate(loan.dueDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {loan.returnDate ? formatDate(loan.returnDate) : '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {historyTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Pagination
                  count={historyTotalPages}
                  page={historyPage}
                  onChange={(_, value) => setHistoryPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <OverdueBooksPage />
      </TabPanel>

      {/* Return Confirmation Dialog */}
      <Dialog open={returnDialogOpen} onClose={handleReturnCancel}>
        <DialogTitle>Xác nhận trả sách</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedLoanIds.length === 1
              ? 'Bạn có chắc chắn muốn trả sách này?'
              : `Bạn có chắc chắn muốn trả ${selectedLoanIds.length} sách đã chọn?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReturnCancel}>Hủy</Button>
          <Button onClick={handleReturnConfirm} variant="contained" color="primary">
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectCancel}>
        <DialogTitle>
          {selectedRequestIds.length > 0
            ? `Từ chối ${selectedRequestIds.length} yêu cầu mượn sách`
            : 'Từ chối yêu cầu mượn sách'
          }
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {selectedRequestIds.length > 0
              ? `Vui lòng nhập lý do từ chối ${selectedRequestIds.length} yêu cầu mượn sách.`
              : 'Vui lòng nhập lý do từ chối yêu cầu mượn sách.'
            }
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Lý do từ chối (tùy chọn)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Nhập lý do từ chối..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel}>Hủy</Button>
          <Button onClick={handleRejectConfirm} variant="contained" color="error">
            Từ chối
          </Button>
        </DialogActions>
      </Dialog>

      {/* Book Request Info Dialog */}
      <Dialog open={bookRequestInfoDialogOpen} onClose={() => setBookRequestInfoDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            {selectedRequestForInfo?.book.title}
          </Typography>
          {selectedRequestForInfo?.book.author && (
            <Typography variant="subtitle1" color="text.secondary" component="div">
              Tác giả: {selectedRequestForInfo.book.author}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRequestForInfo && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mt: 1 }}>
              <Box>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Thông tin yêu cầu
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Người yêu cầu
                      </Typography>
                      <Typography variant="body1">{selectedRequestForInfo.user.fullName || 'N/A'}</Typography>
                      {selectedRequestForInfo.user.email && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedRequestForInfo.user.email}
                        </Typography>
                      )}
                      {selectedRequestForInfo.user.phoneNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedRequestForInfo.user.phoneNumber}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ngày yêu cầu
                      </Typography>
                      <Typography variant="body1">{formatDate(selectedRequestForInfo.requestedDate) || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ngày hết hạn dự kiến
                      </Typography>
                      <Typography variant="body1">{formatDate(selectedRequestForInfo.dueDate) || 'N/A'}</Typography>
                    </Box>
                    {selectedRequestForInfo.reviewer && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Người duyệt
                        </Typography>
                        <Typography variant="body1">{selectedRequestForInfo.reviewer.fullName || 'N/A'}</Typography>
                        {selectedRequestForInfo.reviewedDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {formatDate(selectedRequestForInfo.reviewedDate)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
                {selectedRequestForInfo.rejectionReason && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Lý do từ chối
                    </Typography>
                    <Typography variant="body2" color="error">
                      {selectedRequestForInfo.rejectionReason}
                    </Typography>
                  </Paper>
                )}
                {selectedRequestForInfo.notes && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Ghi chú
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRequestForInfo.notes}
                    </Typography>
                  </Paper>
                )}
              </Box>
              <Box>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Trạng thái sách
                  </Typography>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Số lượng
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequestForInfo.book.availableCopies}/{selectedRequestForInfo.book.totalCopies} có sẵn
                    </Typography>
                  </Box>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Trạng thái yêu cầu
                  </Typography>
                  <Chip
                    label={
                      selectedRequestForInfo.status === 'pending' ? 'Chờ duyệt' :
                        selectedRequestForInfo.status === 'approved' ? 'Đã duyệt' : 'Từ chối'
                    }
                    color={
                      selectedRequestForInfo.status === 'pending' ? 'warning' :
                        selectedRequestForInfo.status === 'approved' ? 'success' : 'error'
                    }
                    size="small"
                  />
                  {selectedRequestForInfo.book.availableCopies <= 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning">
                        Sách này hiện không còn sẵn có. Không thể duyệt yêu cầu mượn.
                      </Alert>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookRequestInfoDialogOpen(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialogs */}
      <ExportDialog
        open={exportCurrentLoansDialogOpen}
        onClose={() => setExportCurrentLoansDialogOpen(false)}
        onExport={handleExportCurrentLoans}
        columns={currentLoansExportColumns}
        fileName="Danh_sach_sach_muon"
        sheetName="Sách mượn"
        title="Xuất danh sách sách mượn ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu sách mượn. Dữ liệu sẽ được xuất theo ngày mượn."
        requireDateRange={true}
      />

      <ExportDialog
        open={exportRequestsDialogOpen}
        onClose={() => setExportRequestsDialogOpen(false)}
        onExport={handleExportRequests}
        columns={requestsExportColumns}
        fileName="Danh_sach_duyet_sach"
        sheetName="Duyệt sách"
        title="Xuất danh sách duyệt sách ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu yêu cầu mượn sách. Dữ liệu sẽ được xuất theo ngày yêu cầu."
        requireDateRange={true}
      />

      <ExportDialog
        open={exportHistoryDialogOpen}
        onClose={() => setExportHistoryDialogOpen(false)}
        onExport={handleExportHistory}
        columns={historyExportColumns}
        fileName="Danh_sach_lich_su_muon"
        sheetName="Lịch sử mượn"
        title="Xuất danh sách lịch sử mượn ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu lịch sử mượn. Dữ liệu sẽ được xuất theo ngày mượn."
        requireDateRange={true}
      />

      {/* Snackbar */}
      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};

